import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { UtilFns } from '@utils';
import { AccountAddress, AccountProcurementCard, AccountPurchaseOrder, ApprovalTreeUserGroup } from '@models';
import { ApprovalTreesComponent } from './approval-trees.component';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { OrgTreeDesignationService } from '../services/org-tree-designation.service';
import { UtilDialogService } from '@core';

export interface IApprovalTreeUserGroupParams {
  parent: any;
  atug: ApprovalTreeUserGroup;
  isEditable: boolean;
  isInventoryProgram?: boolean;
}

@Component({
  selector: 'prox-approval-tree-user-group.dialog',
  standalone: false,
  templateUrl: './approval-tree-user-group.dialog.component.html',
})
export class ApprovalTreeUserGroupDialogComponent implements OnInit {
  isPageReady = false;
  atug!: ApprovalTreeUserGroup;
  parent!: ApprovalTreesComponent;
  isEditable = true;
  isInventoryProgram = false;
  billingAddresses: AccountAddress[] = [];
  pos: AccountPurchaseOrder[] = [];
  pcards: AccountProcurementCard[] = [];

  /**
   * Guard 3. Purchase order, procurement card and ordering time frame are only editable on a
   * program-specific tree. On a shared tree they would apply to every Program using it — the
   * silent cross-program purchase-order overwrite this whole feature exists to stop.
   */
  isProgramSpecific = false;
  isDesignating = false;
  designationNote = '';

  /**
   * Names the Program these settings belong to. This dialog historically named only the group,
   * so an administrator had no way to know whose purchase orders they were editing — which is
   * precisely how the damage happened.
   */
  scopeCaption = '';

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<IApprovalTreeUserGroupParams>,
    public toastr: ToastrService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public designationService: OrgTreeDesignationService,
  ) {}

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params);
    this.parent = params.parent;
    this.atug = params.atug;
    this.isEditable = params.isEditable;
    this.isInventoryProgram = params.isInventoryProgram ?? false;
    this.billingAddresses = this.parent.billingAddresses;
    this.pos = this.parent.pos;
    this.pcards = this.parent.pcards;
    await this.refreshScope();
    this.isPageReady = true;
  }

  /** Recomputes designation state and the caption naming the owning Program. */
  private async refreshScope() {
    const tree = this.atug.approvalTree;
    this.isProgramSpecific = !!tree?.isProgramSpecific;

    if (!this.isProgramSpecific) {
      this.scopeCaption = `Organization Tree: ${tree?.name ?? ''} — shared by any number of Programs`;
      return;
    }

    const owner = await this.designationService.getOwner(this.parent.accountId, tree?.id);
    this.scopeCaption = owner
      ? `Purchase Order and Ordering Window for ${owner.ProgramName}`
      : `Organization Tree: ${tree?.name ?? ''} — program-specific, not yet used by a Program`;
  }

  /**
   * Promote-on-demand, last-resort trigger. This dialog is reachable from the Organization Trees
   * page with no Program context at all, so it is the fallback rather than the front door —
   * the cheapest moment to promote is when the Program's purchase order type is set to
   * "Per Program User Group", long before anyone opens this screen.
   */
  async onMakeProgramSpecific() {
    const tree = this.atug.approvalTree;
    if (!tree) return;

    this.designationNote = '';
    this.isDesignating = true;
    try {
      // checkCanDesignate(), not offerToDesignate(). We are inside a PrimeNG DynamicDialog, and a
      // nested ConfirmDialog never renders here — it is hosted by prox-main and gets lost behind
      // this modal, leaving the promise unresolved and the button apparently dead. No loss: the
      // panel above already explains why, so a second "are you sure?" would be redundant.
      const result = await this.designationService.checkCanDesignate(tree);
      if (!result.ok) {
        this.designationNote = result.message ?? '';
        return;
      }

      // Mutate and save HERE, with this component's own save service. The designation service is
      // providedIn 'root' and holds a different Breeze EntityManager, so saving from there throws
      // "Only entities in this entityManager may be saved".
      tree.isProgramSpecific = true;
      await this.dbSaveService.saveSelectedChanges([tree]);
      await this.refreshScope();
      this.toastr.success(`'${tree.name}' is now a program-specific Organization Tree`, 'Database Activity');
    } finally {
      this.isDesignating = false;
    }
  }

  public static async openDialog(
    dialogService: DialogService,
    data?: IApprovalTreeUserGroupParams,
    config?: DynamicDialogConfig<IApprovalTreeUserGroupParams>,
  ) {
    const ref = dialogService.open(ApprovalTreeUserGroupDialogComponent, {
      // Title goes in PrimeNG's own header, matching ApprovalTreeDialogComponent. With no header
      // PrimeNG still renders an empty header bar, which pushed the title well down the dialog.
      header: data ? `Program User Group: ${data.atug.programUserGroup?.name ?? ''}` : 'Program User Group',
      closable: false,
      draggable: true,
      modal: true,
      width: 'max(50vw, 700px)',
      // No fixed height — the dialog has two very different states. Pinning it at 500px left the
      // shared-tree state (two short paragraphs and a button) as mostly empty white space.
      // Trimmed top padding: closable is false so there is no header bar, and the default padding
      // left the title floating well below the top edge.
      contentStyle: { 'padding-top': '1rem' },
      // baseZIndex: 10000,
      focusOnShow: false,
      ...config,
      data: data,
    });
    const r = await firstValueFrom(ref.onClose);
    return r;
  }

  async onSave() {
    if (await this.isValid(true)) {
      await this.dbSaveService.saveSelectedChanges([this.atug]);
      this.toastr.success('Saved', 'Database Activity');
      this.ref.close(true);
    }
  }

  onCancel() {
    this.atug.entityAspect.rejectChanges();
    this.ref.close(false);
  }

  canSave() {
    return this.atug.entityAspect.entityState.isAddedModifiedOrDeleted();
  }

  async isValid(showErrors: boolean) {
    let _isValid = true;

    if (this.atug.orderTimeFrameStartDays === null && this.atug.orderTimeFrameDurationDays === null) {
      this.atug.orderTimeFrameShipByFromStartDays = undefined;
      return true;
    }

    if (this.atug.orderTimeFrameStartDays && this.atug.orderTimeFrameStartDays < 0) {
      if (showErrors)
        await this.dialogService.warnDialog('Cannot Save', "'Starts After Program Start (Days)' must be blank or a positive number");
      _isValid = false;
    }

    if (this.atug.orderTimeFrameDurationDays && this.atug.orderTimeFrameDurationDays < 0) {
      if (showErrors)
        await this.dialogService.warnDialog('Cannot Save', "'Order Period Duration (Days)' must be blank or a positive number");
      _isValid = false;
    }

    if (this.atug.orderTimeFrameShipByFromStartDays && this.atug.orderTimeFrameShipByFromStartDays < 0) {
      if (showErrors)
        await this.dialogService.warnDialog('Cannot Save', "'Ship Date After Start (Days)' must be blank, zero or a positive number");
      _isValid = false;
    }

    if (this.atug.orderTimeFrameDurationDays && this.atug.orderTimeFrameDurationDays > 0 && this.atug.orderTimeFrameStartDays == null) {
      if (showErrors) await this.dialogService.warnDialog('Cannot Save', "'Starts After Program Start (Days)' value is required");
      _isValid = false;
    }

    if (<number>this.atug.orderTimeFrameStartDays >= 0 && !this.atug.orderTimeFrameDurationDays) {
      if (showErrors) await this.dialogService.warnDialog('Cannot Save', "'Order Period Duration (Days)' value is required");
      _isValid = false;
    }

    return _isValid;
  }

  isReadOnly() {
    // return this.activePrograms.length > 0 || this.parent.isReadOnly();
    return !this.isEditable || this.parent.isReadOnly();
  }

  cancelLabel() {
    return this.isReadOnly() ? 'Close' : 'Cancel';
  }

  isDisabled() {
    return false;
  }
}
